import type * as React from 'react';

import type * as api from '@gitbook/api-client';
import {
    type ContentBranchHistoryItem,
    getRevisionCreationTime,
    listUsersInEdits,
} from '@gitbook/hive-core';
import { DateShortRelative, usePaletteTrigger } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    ListItem,
    ListItemBody,
    ListItemControls,
    ListItemDescription,
    ListItemDetail,
    ListItemIndicator,
} from '@gitbook/spine-react';

import { useNavigation } from '../../navigation';
import { capitalizeFirstLetter } from '../../utils';
import { GitSyncProviderName } from '../GitSyncProviders';
import { UserPrettyNames } from '../Users';
import { HiveRevisionChanges } from './HiveRevisionChanges';
import { HiveRevisionIcon } from './HiveRevisionIcon';
import { useRevisionCommands } from './useRevisionCommands';

/**
 * Item in the history for a revision.
 */
export function HiveBranchHistoryItem(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest | null;
    revisionChanges: ContentBranchHistoryItem;
    active?: boolean;
    readOnly?: boolean;
}): React.ReactElement {
    const { space, changeRequest, revisionChanges, active = false, readOnly } = props;
    const { revision, changes } = revisionChanges;

    const { getNavigationLinkProps } = useNavigation();

    const [palette, rollbackConfirmModal] = useRevisionCommands(space, revision, changeRequest, {
        readOnly,
    });
    const paletteTrigger = usePaletteTrigger({ palette });

    const linkProps = getNavigationLinkProps({
        screen: {
            revision: revision.id,
            changeRequest: changeRequest?.number,
            sideSheet: undefined,
        },
    });

    const users = listUsersInEdits(revision.edits);
    const commitUrl = revision.git?.commit?.url;
    const gitProviderName = space.gitSync?.installationProvider
        ? GitSyncProviderName[space.gitSync.installationProvider]
        : 'Git';
    const createdAt = getRevisionCreationTime(revision);

    return (
        <>
            <ListItem
                {...linkProps}
                active={paletteTrigger.opened}
                selected={active}
                className="my-1"
            >
                <ListItemIndicator>
                    <HiveRevisionIcon
                        space={space}
                        changeRequest={!!changeRequest}
                        revision={revision}
                        changes={changes}
                    />
                </ListItemIndicator>
                <ListItemBody>
                    <ListItemDescription>
                        {revision.git?.commit && !revision.git.commit.createdByGitBook ? (
                            getPrettyCommitMessage(revision.git.commit.message)
                        ) : (
                            <div className="text-base">
                                <strong>
                                    <UserPrettyNames userIds={users} you={false} />
                                </strong>{' '}
                                <HiveRevisionChanges
                                    isDefaultBranch={!changeRequest}
                                    {...revisionChanges}
                                />
                            </div>
                        )}
                    </ListItemDescription>
                    <ListItemDetail>
                        <>
                            <DateShortRelative value={createdAt} onlyDate={false} />
                            {revision.git?.commit && !revision.git.commit.createdByGitBook ? (
                                <> on {gitProviderName}</>
                            ) : null}
                        </>
                    </ListItemDetail>
                </ListItemBody>
                <ListItemControls>
                    {commitUrl ? (
                        <Button
                            key="open-commit"
                            iconOnly
                            kind="nested"
                            label={`View on ${gitProviderName || 'Git'}`}
                            leadingIcon={Icon.ExternalLink}
                            href={commitUrl}
                            target="_blank"
                        />
                    ) : null}
                    <Button
                        {...paletteTrigger.triggerProps}
                        key="options"
                        iconOnly
                        kind="nested"
                        label="Revision options"
                        leadingIcon={Icon.MoreVertical}
                    />
                </ListItemControls>
            </ListItem>
            {rollbackConfirmModal}
        </>
    );
}

/**
 * RegEx to extract the branch name and changes string from a GitSync commit.
 */
const GITBOOK_MESSAGE_EXTRACTOR = /GitBook: \[.*\] (.*)/;

/**
 * Returns a pretty version of the commit message.
 */
function getPrettyCommitMessage(message: string): string {
    let result = message;

    // Commits made by GitBook start with GitBook: and have a specific format
    // If this format matches, extract the commit message and ignore the metadata
    // surrounding it
    if (message.startsWith('GitBook:')) {
        const matches = GITBOOK_MESSAGE_EXTRACTOR.exec(message);
        result = matches ? capitalizeFirstLetter(matches[1]) : message;
    }

    // Truncate to only return the first line
    result = result.split('\n')[0];

    return result;
}
